import { useCallback, useMemo } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { useSelector } from 'react-redux'
import _parseInt from 'lodash/parseInt'
import {
  APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
  APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../apollo-graphql/index'
import { selectCurrentUserProfilePicture } from '../community/identity/profile/redux/selectors'
import {
  COMMENT_LIST_QUERY,
  COMMENT_CREATE_MUTATION,
  COMMENT_UPDATE_MUTATION,
  COMMENT_REPORT_MUTATION,
} from './queries'

const GET_CURRENT_DISPLAY_NAME = gql`
  query CurrentUserDisplayName {
    userProfileCurrent {
      uuid
      displayName
    }
  }
`

/**
 * Hook that provides comment data and mutations via Apollo.
 *
 * @param {Object} options
 * @param {Number|String} options.contentId  The content ID to load comments for
 * @param {Map} options.auth                 Immutable auth Map (reads jwt, uid, username)
 * @returns {{ comments, totalCount, loading, error,
 *   createComment, updateComment, reportComment, refetch }}
 */
export default function useComments ({ contentId, auth }) {
  const jwt = auth.get('jwt')
  const uid = auth.get('uid')
  const username = auth.get('username')
  const isLoggedIn = !!jwt

  const currentUserPicture = useSelector(selectCurrentUserProfilePicture)

  const { data: displayNameData } = useQuery(GET_CURRENT_DISPLAY_NAME, {
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    skip: !isLoggedIn,
    context: { jwt },
  })
  const currentDisplayName = displayNameData?.userProfileCurrent?.displayName
    || username
    || 'Friend of Gaia'

  const parsedContentId = contentId ? _parseInt(contentId) : null

  const {
    data, loading, error, refetch,
  } = useQuery(COMMENT_LIST_QUERY, {
    variables: { contentId: parsedContentId, limit: 250, offset: 0 },
    skip: !parsedContentId,
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: { jwt },
  })

  const comments = data?.commentList?.comments || []
  const totalCount = data?.commentList?.count || 0

  const [createMutation] = useMutation(COMMENT_CREATE_MUTATION)
  const [updateMutation] = useMutation(COMMENT_UPDATE_MUTATION)
  const [reportMutation] = useMutation(COMMENT_REPORT_MUTATION)

  const createComment = useCallback(async (body, pid) => {
    const currentAvatar = currentUserPicture || {}
    await createMutation({
      variables: {
        input: {
          contentId: parsedContentId,
          comment: body,
          subject: '',
          pid: pid ? _parseInt(pid) : 0,
        },
      },
      context: { jwt },
      optimisticResponse: {
        commentCreate: {
          __typename: 'CommentMutationResponse',
          success: true,
          comment: {
            __typename: 'Comment',
            id: `temp-${Date.now()}`,
            uid,
            pid: pid ? _parseInt(pid) : 0,
            contentId: parsedContentId,
            subject: '',
            comment: body,
            status: 'PUBLISHED',
            createdAt: new Date().toISOString(),
            userProfile: {
              __typename: 'UserProfile',
              uuid: displayNameData?.userProfileCurrent?.uuid || null,
              displayName: currentDisplayName,
              avatar: {
                __typename: 'ProfilePicture',
                url: currentAvatar.url || '',
                small: currentAvatar.small || '',
                card: currentAvatar.card || '',
              },
            },
            commentsChildren: [],
          },
        },
      },
      update: (cache, { data: mutationData }) => {
        if (!mutationData?.commentCreate?.success) return
        const newComment = mutationData.commentCreate.comment
        const existing = cache.readQuery({
          query: COMMENT_LIST_QUERY,
          variables: { contentId: parsedContentId, limit: 250, offset: 0 },
        })
        if (!existing?.commentList) return

        if (newComment.pid && newComment.pid > 0) {
          // Reply: insert as child of parent comment
          const updatedComments = existing.commentList.comments.map((c) => {
            if (_parseInt(c.id) === _parseInt(newComment.pid)) {
              return {
                ...c,
                commentsChildren: [...(c.commentsChildren || []), newComment],
              }
            }
            return c
          })
          cache.writeQuery({
            query: COMMENT_LIST_QUERY,
            variables: { contentId: parsedContentId, limit: 250, offset: 0 },
            data: {
              commentList: {
                ...existing.commentList,
                comments: updatedComments,
                count: existing.commentList.count + 1,
              },
            },
          })
        } else {
          // Root comment: prepend to list
          cache.writeQuery({
            query: COMMENT_LIST_QUERY,
            variables: { contentId: parsedContentId, limit: 250, offset: 0 },
            data: {
              commentList: {
                ...existing.commentList,
                comments: [newComment, ...existing.commentList.comments],
                count: existing.commentList.count + 1,
              },
            },
          })
        }
      },
    })
  }, [
    createMutation, parsedContentId, jwt, uid,
    currentDisplayName, currentUserPicture, displayNameData,
  ])

  const updateComment = useCallback(async (commentId, body) => {
    await updateMutation({
      variables: { input: { id: String(commentId), comment: body } },
      context: { jwt },
    })
    refetch()
  }, [updateMutation, jwt, refetch])

  const reportComment = useCallback(async (commentId) => {
    const { data: reportData } = await reportMutation({
      variables: { id: String(commentId) },
      context: { jwt },
    })
    return reportData?.commentReport?.success || false
  }, [reportMutation, jwt])

  return useMemo(() => ({
    comments,
    totalCount,
    loading,
    error,
    createComment,
    updateComment,
    reportComment,
    refetch,
  }), [comments, totalCount, loading, error, createComment, updateComment, reportComment, refetch])
}
